/*
 * Selectors for accessing staff memberships specific state data.
 *
 * Computed data is memoized and will only be recomputed when related state data changes.
 */
import _ from 'lodash';

import { createSelector } from '../../common/memoizer';
import type { StaffManagementState } from '../store';
import typeToScopeTypeName from '../typeToScopeTypeName';
import type {
  ObjectId,
  StaffMembershipInterface,
  StaffMembershipRoleInterface,
  StaffMembershipType,
  StaffMembershipUserInterface,
} from '../types';

export const getFromId = (state?: StaffManagementState) => state?.fromId;
export const getType = (state?: StaffManagementState) => state?.type;
export const getMembershipsById = (state?: StaffManagementState) => state?.data?.memberships;
export const getRolesById = (state?: StaffManagementState) => state?.data?.roles;
export const getUsersById = (state?: StaffManagementState) => state?.data?.users;

/** Assocative array of all memberships indexed and grouped by userId. */
export const getMembershipsByUserId = createSelector(
  getMembershipsById,
  (membershipsById: { [key: string]: StaffMembershipInterface }) => {
    return _.groupBy(membershipsById, (membership) => membership.userId);
  }
);

/** Associative array of all memberships indexed and grouped by roleId. */
export const getMembershipsByRoleId = createSelector(
  getMembershipsById,
  (membershipsById: { [key: string]: StaffMembershipInterface }) => {
    return _.groupBy(membershipsById, (membership) => membership.roleId);
  }
);

/** Array of roles limited to the subset of roles actually used by the users. */
export const getRolesLimitedByMemberships = createSelector(
  getMembershipsByRoleId,
  getRolesById,
  (
    membershipsByRoleId: { [key: string]: StaffMembershipInterface },
    rolesById: { [key: string]: StaffMembershipRoleInterface }
  ) => {
    return _.filter(rolesById, (role) => !!membershipsByRoleId[role.id]);
  }
);

/** Assocative array of all memberships indexed by user email. */
export const getMembershipsByUserEmail = createSelector(
  getUsersById,
  getMembershipsById,
  (
    usersById: { [id: string]: StaffMembershipUserInterface },
    membershipsById: { [id: string]: StaffMembershipInterface }
  ) => {
    // DG-1300: By original contract this extra check should have never been needed as we should have
    // received 1 user entity for every distinct membership.userId. Due to "real life," it seems we
    // may not always be able to trust in this contract, and a quick modification to the Frontend
    // is cheaper while waiting for a more robust fix to the BE to prevent GIGO.
    // If a membership does not have a user reference, filter it out.
    const membershipsWithUsers = _.filter(membershipsById, (membership) => !!usersById[membership.userId]);
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return _.groupBy(membershipsWithUsers, (membership) => usersById[membership.userId].email);
  }
);

/** Assocative array of all memberships indexed by user id, limited by type (partner, program, etc.) */
export const getMembershipsByUserIdLimitedByType = createSelector(
  getFromId,
  getMembershipsByUserId,
  getType,
  (
    fromId: ObjectId,
    membershipsByUserId: { [key: string]: Array<StaffMembershipInterface> },
    type: StaffMembershipType
  ) => {
    const result = _.cloneDeep(membershipsByUserId);
    _.forEach(_.keys(result), (key) => {
      result[key] = _.filter(
        result[key],
        (membership) =>
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          _.get(membership, 'scope.typeName') === typeToScopeTypeName[type as string] &&
          _.get(membership, 'scope.id') === fromId
      );
    });
    return result;
  }
);

/** Assocative array of all memberships indexed by email, limited by type (partner, program, etc.) */
export const getMembershipsByUserEmailLimitedByType = createSelector(
  getFromId,
  getMembershipsByUserEmail,
  getType,
  (
    fromId: ObjectId,
    membershipsByUserEmail: { [email: string]: Array<StaffMembershipInterface> },
    type: StaffMembershipType
  ) => {
    const result = _.cloneDeep(membershipsByUserEmail);
    _.forEach(_.keys(result), (key) => {
      result[key] = _.filter(
        result[key],
        (membership) =>
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          _.get(membership, 'scope.typeName') === typeToScopeTypeName[type as string] &&
          _.get(membership, 'scope.id') === fromId
      );
    });
    return result;
  }
);
